import { z } from 'zod'

const envSchema = z.object({
  // Base URL for the app (Vite's BASE_URL)
  BASE_URL: z.string().default('/'),

  // Keycloak - with defaults for development
  VITE_KEYCLOAK_URL: z
    .string()
    .url({ message: 'Invalid Keycloak URL' })
    .default('http://localhost:8080'),
  VITE_KEYCLOAK_REALM: z.string().min(1).default('licenseserver'),
  VITE_KEYCLOAK_CLIENT_ID: z.string().min(1).default('licenseserver-web'),

  // API - with defaults for development
  VITE_API_BASE_URL: z
    .string()
    .url({ message: 'Invalid API URL' })
    .default('https://localhost:7500'),
  VITE_API_OPENAPI_URL: z.string().url({ message: 'Invalid OpenAPI URL' }).optional(),
  VITE_API_PROXY_URL: z.string().url({ message: 'Invalid proxy URL' }).optional(),

  // App
  VITE_APP_NAME: z.string().default('Modern Development Solutions | Licenses'),
  VITE_APP_VERSION: z.string().default('1.0.0'),

  // Public installer-docs site — surfaced in customer activation instructions
  // so they can read how to install the Panel before activating.
  VITE_INSTALL_DOCS_URL: z
    .string()
    .url({ message: 'Invalid install-docs URL' })
    .default('https://license-install.mod-sol-sa.com'),

  // Feature Flags - Fixed: default must be applied before transform
  VITE_ENABLE_PWA: z
    .string()
    .default('false')
    .transform(v => v === 'true'),
  VITE_ENABLE_MONITORING: z
    .string()
    .default('false')
    .transform(v => v === 'true'),
  VITE_ENABLE_DEVTOOLS: z
    .string()
    .default('true')
    .transform(v => v === 'true'),

  // Monitoring (Optional)
  VITE_SENTRY_DSN: z.string().url({ message: 'Invalid Sentry DSN URL' }).optional(),
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_DATADOG_CLIENT_TOKEN: z.string().optional(),
})

export type Env = z.infer<typeof envSchema>

// Runtime config injected by docker-entrypoint.sh into window.__ENV
// Falls back to import.meta.env for Vite dev server
const runtimeEnv = (window as unknown as Record<string, unknown>).__ENV as
  | Record<string, string | undefined>
  | undefined

function getEnv(key: string, fallback?: string): string | undefined {
  const runtime = runtimeEnv?.[key]
  if (runtime !== undefined && runtime !== '') return runtime

  const buildTime = (import.meta.env as Record<string, string | undefined>)[key]
  if (buildTime !== undefined && buildTime !== '') return buildTime

  return fallback
}

const envObject = {
  BASE_URL: import.meta.env.BASE_URL || '/',

  VITE_KEYCLOAK_URL: getEnv('VITE_KEYCLOAK_URL', 'http://localhost:8080'),
  VITE_KEYCLOAK_REALM: getEnv('VITE_KEYCLOAK_REALM', 'licenseserver'),
  VITE_KEYCLOAK_CLIENT_ID: getEnv('VITE_KEYCLOAK_CLIENT_ID', 'licenseserver-web'),

  VITE_API_BASE_URL: getEnv('VITE_API_BASE_URL', 'https://localhost:7500'),
  VITE_API_OPENAPI_URL: getEnv('VITE_API_OPENAPI_URL'),
  VITE_API_PROXY_URL: getEnv('VITE_API_PROXY_URL'),
  VITE_APP_NAME: getEnv('VITE_APP_NAME', 'Modern Development Solutions | Licenses'),
  VITE_APP_VERSION: getEnv('VITE_APP_VERSION', '1.0.0'),
  VITE_INSTALL_DOCS_URL: getEnv('VITE_INSTALL_DOCS_URL', 'https://license-install.mod-sol-sa.com'),
  VITE_ENABLE_PWA: getEnv('VITE_ENABLE_PWA', 'false'),
  VITE_ENABLE_MONITORING: getEnv('VITE_ENABLE_MONITORING', 'false'),
  VITE_ENABLE_DEVTOOLS: getEnv('VITE_ENABLE_DEVTOOLS', 'true'),
  VITE_SENTRY_DSN: getEnv('VITE_SENTRY_DSN'),
  VITE_POSTHOG_KEY: getEnv('VITE_POSTHOG_KEY'),
  VITE_DATADOG_CLIENT_TOKEN: getEnv('VITE_DATADOG_CLIENT_TOKEN'),
}

// Parse and validate
const parseResult = envSchema.safeParse(envObject)

// Export the validated environment
export const env: Env = parseResult.success ? parseResult.data : envSchema.parse(envObject) // This will throw with helpful error messages

if (!parseResult.success) {
  console.warn('Environment validation issues:', parseResult.error.issues)
  console.warn('Using default values for missing environment variables')
}

// Log the final configuration (without sensitive data)
console.log('Environment configuration loaded:', {
  keycloakUrl: env.VITE_KEYCLOAK_URL,
  keycloakRealm: env.VITE_KEYCLOAK_REALM,
  keycloakClientId: env.VITE_KEYCLOAK_CLIENT_ID,

  apiBaseUrl: env.VITE_API_BASE_URL,
  appName: env.VITE_APP_NAME,
  appVersion: env.VITE_APP_VERSION,
})
